<script setup lang="ts">
import type { UIAlertProps } from '../../types'
import { onMounted, useAttrs } from '#imports'

const props = withDefaults(defineProps<UIAlertProps>(), {
    icon: undefined,
    duration: 0,
    closeable: false,
    message: undefined,
    type: 'danger',
    isReminder: false
})

const attrs = useAttrs()

const emit = defineEmits([
    'mounted',
    'closed'
])

const modelValue = defineModel<boolean>({ default: true })

const close = () => {
    emit('closed', {
        attrs,
        props
    })

    modelValue.value = false
}

const onCloseClick = attrs.onCloseClicked as () => void ?? close

onMounted(() => {
    emit('mounted', {
        attrs,
        props
    })

    if (props.duration > 0) {
        setTimeout(close, props.duration)
    }
})
</script>

<template>
    <div
        v-show="modelValue"
        :class="['alert', `alert--${type}`, { 'alert--reminder': isReminder, 'alert--has-actions': $slots.actions }]"
    >
        <UIButton
            v-if="closeable"
            icon
            class="alert__btn--close"
            @click.prevent="onCloseClick"
        >
            <UIIcon
                name="close"
                class="alert__icon"
                size="12"
            />
        </UIButton>

        <div class="alert__message">
            <UIIcon
                v-if="icon"
                :name="icon"
                class="alert__icon"
                size="20"
            />
            <div>
                <slot>
                    <p>
                        {{ message }}
                    </p>
                </slot>
            </div>
        </div>

        <div v-if="$slots.actions" class="alert__actions">
            <slot name="actions" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@mixin alert($color, $background, $border) {
    color: var(--alert-color, #{$color});
    background: var(--alert-bg, #{$background});
    border: 1px solid var(--alert-border, #{$border});
}

.alert {
    padding: 12px;
    position: relative;
    font-size: $font-size-sm;
    z-index: 3;
    box-shadow: var(--alert-shadow, none);

    display: grid;
    grid-template-areas: 'message close';
    gap: 8px;
    grid-template-columns: 1fr min-content;

    &--has-actions {
        grid-template-areas:
            'message close'
            'actions close';
    }

    :deep(p),
    :deep(h4) {
        color: inherit;
        margin: 0 0 5px;
        padding: 0;
        font-size: inherit;
    }

    // UIButton renders as an anchor when given href/to; those keep their button colours.
    :deep(a:not(.btn)) {
        color: var(--alert-link-color, var(--on-link, $link-color));
        text-decoration: underline;
        text-underline-offset: 2px;

        &:hover,
        &:focus {
            color: var(--alert-link-hover-color, var(--on-link-hover, $link-color));
            text-decoration: underline;
        }
    }

    &:not(.alert--reminder) {
        border-radius: var(--border-radius, $border-radius);
    }

    &__message {
        grid-area: message;
        margin: 0;
        display: flex;
        align-items: flex-start;
        line-height: 1.2;
        word-break: break-word;

        :deep(p:last-child) {
            margin: 0;
        }

        .alert__icon {
            color: var(--alert-icon-color, inherit);
            filter: var(--alert-icon-filter, none);
        }
    }

    &__icon {
        margin: 0 15px 1px 0;
    }

    &__actions {
        grid-area: actions;
        display: flex;
        margin-top: 10px;
        gap: 10px;

        .btn {
            width: 100%;

            @media (min-width: $screen-sm) {
                width: auto;
            }
        }
    }

    &--info {
        --alert-accent: var(--info);
        @include alert(
            var(--on-info-soft, $brand-info-text),
            var(--info-soft, $brand-info-bg),
            var(--border-info-soft, $brand-info-border)
        );
    }

    &--success {
        --alert-accent: var(--success);
        @include alert(
            var(--on-success-soft, $brand-success-text),
            var(--success-soft, $brand-success-bg),
            var(--border-success-soft, $brand-success-border)
        );
    }

    &--warning {
        --alert-accent: var(--warning);
        margin-bottom: 8px;
        @include alert(
            var(--on-warning-soft, $brand-warning-text),
            var(--warning-soft, $brand-warning-bg),
            var(--border-warning-soft, $brand-warning-border)
        );
    }

    &--danger {
        --alert-accent: var(--danger);
        @include alert(
            var(--on-danger-soft, $brand-danger-text),
            var(--danger-soft, $brand-danger-bg),
            var(--border-danger-soft, $brand-danger-border)
        );
    }

    &--reminder {
        border-width: 1px;
        border-radius: var(--border-radius, $border-radius);
        @media (min-width: $screen-lg) {
            &:first-child:nth-last-child(2),
            &:first-child:nth-last-child(3) {
                border-radius: var(--border-radius, $border-radius) var(--border-radius, $border-radius) 0 0;
            }

            &:last-child:nth-child(2),
            &:nth-child(2):nth-last-child(2) {
                border-radius: 0 0 var(--border-radius, $border-radius) var(--border-radius, $border-radius);
            }
        }
    }

    .alert__btn--close.btn--icon {
        grid-area: close;
        align-self: start;
        justify-self: end;
        color: inherit;
        color: color-mix(in srgb, currentColor 50%, transparent);
        padding: unset;
        transition: color 0.2s;

        // The shared .alert__icon margin would widen the pill-shaped button and push the X off the edge.
        .alert__icon {
            margin: 0;
        }

        &:hover,
        &:focus,
        &:active {
            background-color: transparent;
            color: inherit;
        }
    }
}
</style>
